import {
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ClearRounded,
  PlaylistAddRounded,
  UpdateRounded
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  ApprovalType,
  getApprovalTypesAsync
} from '../ApprovalType/approvalTypeSlice';
import {
  createApprovalCodeAsync,
  getApprovalCodeAsync,
  updateApprovalCodeAsync
} from './approvalCodeSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    columnFullWidth: {
      width: '100%',
      marginBottom: theme.spacing(2)
    }
  })
);

interface CreateEditApprovalCode {
  id: number | null;
  name: string | null;
  approvalTypes: ApprovalType[];
}

export default function ApprovalCodeCreateEdit() {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { selectedApprovalCode } = useSelector(
    (state: RootState) => state.approvalCodes
  );
  const { data: approvalTypes } = useSelector(
    (state: RootState) => state.approvalTypes
  );

  const params = useParams<{ approvalCodeId: string }>();
  const approvalCodeId = Number.parseInt(params.approvalCodeId, 10);
  const isNewPage = !Boolean(approvalCodeId);

  const [approvalCode, setApprovalCode] = useState<CreateEditApprovalCode>({
    id: null,
    name: null,
    approvalTypes: []
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedApprovalCode) {
      setApprovalCode({
        ...approvalCode,
        id: selectedApprovalCode.id,
        name: selectedApprovalCode.name,
        approvalTypes: selectedApprovalCode.approvalTypes
      });
    }
  }, [selectedApprovalCode]);

  useEffect(() => {
    if (!isNewPage) dispatch(getApprovalCodeAsync(approvalCodeId));
    dispatch(getApprovalTypesAsync());
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setApprovalCode({
      ...approvalCode,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (approvalCode.name) {
        if (isNewPage) {
          // create approval code
          const createApprovalCode = {
            name: approvalCode.name,
            approvalTypeIds: approvalCode.approvalTypes.map(
              (approvalType) => approvalType.id
            )
          };
          await dispatch(createApprovalCodeAsync(createApprovalCode)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('approvalCodeCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update approval type
          const updateApprovalCode = {
            id: approvalCode.id,
            name: approvalCode.name,
            approvalTypeIds: approvalCode.approvalTypes.map(
              (approvalType) => approvalType.id
            )
          };
          await dispatch(updateApprovalCodeAsync(updateApprovalCode)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('approvalCodeCreateEdit.messages.updateSuccess')
            })
          );
        }
      } else {
        throw new Error(t('approvalCodeCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('approvalCodeCreateEdit.breadcrumb.masterDataName') as string,
          masterDataRef: 'approval-codes',
          masterDataSubName: selectedApprovalCode?.name,
          masterDataSubRef: selectedApprovalCode?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('approvalCodeCreateEdit.title.new') : t('approvalCodeCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('approvalCodeCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            className={classes.columnFullWidth}
            id="approval-code-name"
            label={t('approvalCodeCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={approvalCode.name ? approvalCode.name : ''}
            onChange={onChangeField}
            required
          />
          <Autocomplete
            id="ApprovalTypes"
            size="small"
            multiple
            disableCloseOnSelect
            options={approvalTypes}
            getOptionLabel={(option) => option?.name}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  variant="outlined"
                  size="small"
                  deleteIcon={<ClearRounded />}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label={t('approvalCodeCreateEdit.labels.approvalType')} />
            )}
            value={approvalCode.approvalTypes}
            getOptionSelected={(option, value) => option.id === value?.id}
            onChange={(event, newValue) =>
              setApprovalCode({
                ...approvalCode,
                approvalTypes: newValue
              })
            }
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('approvalCodeCreateEdit.buttons.add') : t('approvalCodeCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
